import React, { useState } from 'react';
import { createSystemExplanation } from '../../../../Services/SystemExplanationsApi';
import {
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Divider,
    CircularProgress,
    Chip,
} from '@mui/material';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import InfoIcon from "@mui/icons-material/Info";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from "@mui/icons-material/Image";

const AddSystemExplanations = ({ onSuccess }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: '',
        is_active: '',
        image: null,
        version: '',
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData({ ...formData, image: file });
        if (file) {
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        } else {
            setImagePreview(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.image) {
            alert("❌ الرجاء تحميل صورة قبل الإرسال.");
            return;
        }
        setSubmitting(true);
        try {
            const formDataToSend = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== "") {
                    formDataToSend.append(key, formData[key]);
                }
            });

            await createSystemExplanation(formDataToSend);
            alert("✅ تم إنشاء شرح النظام بنجاح!");
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error("Error creating system explanation:", error);
            alert("❌ فشل في إنشاء شرح النظام.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                {/* العنوان والوصف */}
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#1976d2' }}>
                                    <TitleIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    المعلومات الأساسية
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="عنوان الشرح"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        sx={{ bgcolor: 'white' }}
                                        placeholder="أدخل عنوان الشرح..."
                                        InputProps={{
                                            startAdornment: <TitleIcon sx={{ mr: 1, color: '#1976d2' }} />
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="وصف الشرح"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        fullWidth
                                        required
                                        multiline
                                        rows={6}
                                        variant="outlined"
                                        sx={{ bgcolor: 'white' }}
                                        placeholder="أدخل وصف تفصيلي للشرح..."
                                        InputProps={{
                                            startAdornment: <DescriptionIcon sx={{ mr: 1, color: '#1976d2', alignSelf: 'flex-start', mt: 1 }} />
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* النوع والحالة */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#2196f3' }}>
                                    <CategoryIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    نوع الشرح
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <FormControl fullWidth required sx={{ bgcolor: 'white' }}>
                                <InputLabel>اختر النوع</InputLabel>
                                <Select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    label="اختر النوع"
                                >
                                    <MenuItem value="">
                                        <em>اختر النوع</em>
                                    </MenuItem>
                                    <MenuItem value="info">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InfoIcon color="info" fontSize="small" />
                                            معلومات (Info)
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="warning">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InfoIcon color="warning" fontSize="small" />
                                            تحذير (Warning)
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="error">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InfoIcon color="error" fontSize="small" />
                                            خطأ (Error)
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="feature">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InfoIcon color="success" fontSize="small" />
                                            ميزة (Feature)
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#9c27b0' }}>
                                    <ToggleOnIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                                    حالة الشرح
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <FormControl fullWidth required sx={{ bgcolor: 'white' }}>
                                <InputLabel>اختر الحالة</InputLabel>
                                <Select
                                    name="is_active"
                                    value={formData.is_active}
                                    onChange={handleChange}
                                    label="اختر الحالة"
                                >
                                    <MenuItem value="">
                                        <em>اختر الحالة</em>
                                    </MenuItem>
                                    <MenuItem value="1">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label="نشط" color="success" size="small" />
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="0">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label="غير نشط" color="default" size="small" />
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </CardContent>
                    </Card>
                </Grid>

                {/* الإصدار */}
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#4caf50' }}>
                                    <InfoIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                    معلومات الإصدار
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <TextField
                                label="رقم الإصدار"
                                name="version"
                                value={formData.version}
                                onChange={handleChange}
                                fullWidth
                                required
                                variant="outlined"
                                sx={{ bgcolor: 'white' }}
                                placeholder="مثال: 1.0.0"
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* تحميل الصورة */}
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: '#fff3e0' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#ff9800' }}>
                                    <CloudUploadIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                                    صورة الشرح
                                </Typography>
                                <Chip label="مطلوب" color="error" size="small" sx={{ ml: 1 }} />
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<CloudUploadIcon />}
                                sx={{ 
                                    py: 2,
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    borderColor: '#ff9800',
                                    color: '#ff9800',
                                    '&:hover': {
                                        borderColor: '#f57c00',
                                        bgcolor: '#fff3e0'
                                    }
                                }}
                            >
                                {formData.image ? 'تغيير الصورة' : 'اختر صورة للشرح'}
                                <input
                                    type="file"
                                    name="image"
                                    hidden
                                    onChange={handleFileChange}
                                    accept="image/*"
                                />
                            </Button>
                            {formData.image && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Chip 
                                        label={`✓ تم اختيار: ${formData.image.name}`}
                                        color="success"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </Box>
                            )}
                            {imagePreview && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        معاينة الصورة:
                                    </Typography>
                                    <Box
                                        component="img"
                                        src={imagePreview}
                                        alt="Preview"
                                        sx={{
                                            width: '100%',
                                            maxHeight: 250,
                                            objectFit: 'contain',
                                            borderRadius: 2,
                                            border: '2px solid #ff9800',
                                            boxShadow: 2,
                                            bgcolor: 'white',
                                            p: 1
                                        }}
                                    />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* زر الإرسال */}
                <Grid item xs={12}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        sx={{
                            py: 2,
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #1976d2 30%, #1e88e5 90%)',
                                transform: 'scale(1.02)',
                                transition: 'all 0.2s'
                            },
                            '&:disabled': {
                                background: '#e0e0e0'
                            }
                        }}
                    >
                        {submitting ? 'جاري الإرسال...' : 'إضافة الشرح'}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddSystemExplanations;
